import { motion } from 'framer-motion';
import styled, { css, keyframes } from 'styled-components';

import {
    floorGameColor as color,
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledLayout,
    styledReset,
} from '@pkg/primitives';

import { tickTransition, TickVariant, tickVariants } from '../../../../TicksMeter.motion';
import { TickClipPathDefaultId } from '../../../../clipPaths';
import { tokens } from '../../../../tokens';

interface StyledTickProps {
    $disabled?: boolean;
    $highlight?: boolean;
}

const tickClipPathReference = `#${TickClipPathDefaultId}`;

const tickLiftedStyles = css`
    --yat_ticks-meter-action-width: 162%;
    --yat_ticks-meter-action-height: 140%;

    --yat_ticks-meter-thumbnail-opacity: 1;
    z-index: ${tokens.zIndex.lifted};
`;

const strokePulse = keyframes`
    from {
        fill: rgba(255, 255, 255, 0);
        stroke-width: ${tokens.tick.stroke.pulse.from};
    }

    to {
        fill: rgba(255, 255, 255, 0.1);
        stroke-width: ${tokens.tick.stroke.pulse.to};
    }
`;

export const StyledTick = styled(motion.li).attrs({
    variants: tickVariants,
    animate: TickVariant.Active,
    transition: tickTransition,
})<StyledTickProps>`
    --yat_ticks-meter-action-width: 100%;
    --yat_ticks-meter-action-height: 100%;
    --yat_ticks-meter-thumbnail-opacity: 0;
    --yat_ticks-meter-focus-ring-opacity: 0;

    position: relative;
    z-index: ${tokens.zIndex.tick};
    display: grid;
    place-content: center;
    place-items: center;
    flex: 1 1 auto;
    // This is only applied when nested within a <Segment minWidthTicks />.
    min-width: var(--yat_ticks-meter-tick-min-width, 0);
    // We must use 'drop-shadow()' to apply a shadow effect.
    // 'box-shadow' will not conform to the underlying 'clip-path'.
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.2));
    transition: z-index ${duration.normal}ms ${easing.cubic};

    // Since each 'item' is a rectangle with a parallelogram clip-path,
    // we end up with more whitespace between items than we want.
    &:not(:first-child) {
        margin-left: -${tokens.tick.gapOffset}px;
    }

    ${({ $disabled }) => {
        if ($disabled) {
            return css`
                ${styledInteraction.interactionDisable};
            `;
        }

        if (!$disabled) {
            return css`
                &:hover {
                    ${tickLiftedStyles};
                }
            `;
        }
    }};

    ${({ $highlight }) =>
        $highlight &&
        css`
            ${tickLiftedStyles};
            --yat_ticks-meter-focus-ring-opacity: 1;
            --yat_ticks-meter-focus-ring-animation-name: ${strokePulse};
        `};
`;

export const Action = styled.button.attrs({ type: 'button' })`
    ${styledReset.button};
    z-index: ${tokens.zIndex.action};
    position: absolute;
    width: var(--yat_ticks-meter-action-width);
    height: var(--yat_ticks-meter-action-height);
    clip-path: url(${tickClipPathReference});
    background-color: ${color.cementWet};
    // Animating 'width/height' because the "scale" is non-uniform.
    // Since we have a nested image within the <Tick />, we do not
    // want to "stretch" that image disproportionately.
    transition-property: width, height;
    transition-duration: ${duration.normal}ms;
    transition-timing-function: ${easing.cubic};

    &:focus-visible {
        --yat_ticks-meter-focus-ring-opacity: 1;
    }
`;

export const ThumbnailWrapper = styled.div`
    position: relative;
    z-index: ${tokens.zIndex.thumbnail};
    width: 100%;
    height: 100%;
    opacity: var(--yat_ticks-meter-thumbnail-opacity);
    transition: opacity ${duration.normal}ms ${easing.cubic};
`;

export const FocusRing = styled.div`
    ${styledLayout.positionCover()};
    ${styledInteraction.interactionDisable};
    z-index: ${tokens.zIndex.focusRing};
    opacity: var(--yat_ticks-meter-focus-ring-opacity);
    transition: opacity ${duration.normal}ms ${easing.cubic};

    svg {
        // Cannot use 'styledMedia.iconwrapper',
        // as 'overflow: visible;' will break the clip-path.
        width: 100%;
        height: 100%;
        fill: transparent;
        stroke: white;
        stroke-width: ${tokens.tick.stroke.width};
        stroke-linecap: round;
        stroke-linejoin: round;
        animation-name: var(--yat_ticks-meter-focus-ring-animation-name);
        animation-duration: ${duration.slowest}ms;
        animation-timing-function: ${easing.cubic};
        animation-direction: alternate;
        animation-fill-mode: both;
        animation-iteration-count: infinite;
    }
`;
